/**
 * `ImageUploadField` — campo di upload di un'immagine (es. badge di una
 * certificazione) tramite presigned URL.
 *
 * Flusso (soluzione presigned):
 *  1. l'utente seleziona un file immagine;
 *  2. si richiede all'API un URL di upload firmato
 *     (`POST /api/certifications/image/presign`);
 *  3. il file viene caricato con un `PUT` diretto su S3 verso l'URL firmato;
 *  4. l'URL pubblico restituito dall'API viene propagato via `onChange` e
 *     salvato dal form come `imageUrl` della certificazione.
 *
 * Il componente è controllato: `value` è l'URL pubblico corrente (o vuoto).
 * Mostra un'anteprima quando presente e consente di rimuovere l'immagine.
 */
import { useId, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import type { PresignCertificationImageResponse } from '../shared/dto';
import { Alert, Button } from './ui';

interface ImageUploadFieldProps {
  /** URL pubblico corrente dell'immagine (o stringa vuota se assente). */
  value: string;
  /** Invocata con il nuovo URL pubblico (o '' quando l'immagine viene rimossa). */
  onChange: (publicUrl: string) => void;
  /** Client API per richiedere il presigned URL. */
  api: ApiClient;
  /** Etichetta accessibile del pulsante di selezione file. */
  label?: string;
}

/** Content-type immagine accettati dal backend (deve restare allineato). */
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';

export function ImageUploadField({
  value,
  onChange,
  api,
  label = 'Carica immagine',
}: ImageUploadFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    setIsUploading(true);
    try {
      // 1) Richiede l'URL di upload firmato all'API (autorizzazione admin).
      const presigned = await api.post<PresignCertificationImageResponse>(
        '/api/certifications/image/presign',
        { contentType: file.type },
      );

      // 2) PUT diretto su S3. NB: si usa `fetch` nativo (non il client API), che
      // non deve allegare l'Authorization Bearer né alterare gli header: S3
      // valida la firma sul solo `Content-Type`, che deve combaciare.
      const putResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presigned.contentType },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload non riuscito (HTTP ${putResponse.status})`);
      }

      // 3) Propaga l'URL pubblico al form.
      onChange(presigned.publicUrl);
    } catch {
      setError('Caricamento dell\'immagine non riuscito. Riprova.');
    } finally {
      setIsUploading(false);
      // Consente di ricaricare lo stesso file dopo un errore/rimozione.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPTED_TYPES}
        className="image-upload__input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      <div className="image-upload__row">
        {value !== '' && (
          <img
            src={value}
            alt="Anteprima dell'immagine caricata"
            className="image-upload__preview"
            width={64}
            height={64}
          />
        )}
        <div className="image-upload__actions">
          <Button
            type="button"
            size="sm"
            onClick={() => inputRef.current?.click()}
            loading={isUploading}
            disabled={isUploading}
          >
            {value !== '' ? 'Sostituisci immagine' : label}
          </Button>
          {value !== '' && !isUploading && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange('')}
            >
              Rimuovi
            </Button>
          )}
        </div>
      </div>

      {error !== null && <Alert tone="error">{error}</Alert>}
    </div>
  );
}

export default ImageUploadField;
